// Common
import DataTable from "@components/DataTable";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card } from "react-bootstrap";
import { faTrashCan, faSpinner } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import ConfirmationModal from "@components/Modals/ConfirmationModal";
import { useDeleteOpenRouterKeyMutation } from "@generated";
import { useAuthorization } from "../../../../common/providers/AuthorizationProvider";
// OpenRouterKey Specific
import useListOpenRouterKeys from "./api";
import { OpenRouterKey } from "./types";
import { KEY_TYPE_OPTIONS } from "../common/types";

const List = (): JSX.Element => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const navigate = useNavigate();
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const authorized = useAuthorization();
  const [deleteOpenRouterKey, {}] = useDeleteOpenRouterKeyMutation();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const { openRouterKeys, pageMeta, isLoading, refetch } =
    useListOpenRouterKeys({
      page: {
        number: pageNumber,
        size: pageSize,
      },
    });

  const handleDelete = () => {
    setShowConfirmationModal(true);
  };

  const handleConfirm = () => {
    selectedIds.forEach((keyId) => {
      deleteOpenRouterKey({
        slug: accountSlug,
        id: keyId as unknown as number,
      });
    });
    setSelectedIds([]);
    refetch();
    setShowConfirmationModal(false);
  };

  const handleCancel = () => {
    setShowConfirmationModal(false);
  };

  const getColumns = (): ColumnDef<OpenRouterKey>[] => {
    if (!authorized || isLoading) {
      return [
        {
          header: "Key Type",
          cell(props) {
            return (
              <FontAwesomeIcon icon={faSpinner} spin />
            );
          },
        },
        {
          header: "Identifier",
          cell(props) {
            return (
              <FontAwesomeIcon icon={faSpinner} spin />
            );
          },
        },
      ];
    }

    if (openRouterKeys.length === 0) {
      return [
        {
          header: "Key Type",
          id: "empty",
          cell() {
            return <div>No Open Router Keys configured</div>;
          },
        },
      ];
    }

    return [
      {
        header: "Key Type",
        cell(props) {
          return (
            <div
              className="openRouterKeyName"
              onClick={() =>
                navigate(
                  `/admin/open_router_keys/${props.row.original.id}/edit`
                )
              }
            >
              {KEY_TYPE_OPTIONS.find(
                (opt) => opt.value === props.row.original.keyType
              )?.label || props.row.original.keyType}
            </div>
          );
        },
      },
      {
        header: "Identifier",
        cell(props) {
          return <div>{props.row.original.identifier}</div>;
        },
      },
    ];
  };

  const columns = getColumns();
  const hasData = !!authorized && !isLoading && openRouterKeys.length > 0;

  return (
      <div className="openRouterKeysTable">
        <Card
          style={{
            padding: "10px",
            borderRadius: "4px",
            border: "1px solid var(--gray-3-stroke, #D7D7D7)",
            background: " var(--white, #FFF)",
            boxShadow: "0px 2px 3px 0px rgba(0, 0, 0, 0.15)",
          }}
        >
          <Card.Header
            style={{
              backgroundColor: "#fff",
              lineHeight: "unset",
              display: "block",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div className="accordianTitle" style={{ margin: "0" }}>
                Open Router Keys
              </div>
              <Button
                className="addBtn"
                onClick={() => navigate("/admin/open_router_keys/new")}
              >
                Add An Open Router Key
              </Button>
            </div>
            <hr></hr>
            <div
              style={{
                display: "flex",
              }}
            >
              {selectedIds.length > 0 && (
                <Button
                  style={{
                    marginRight: "10px",
                    alignSelf: "flex-end",
                    background: "unset",
                    color: "var(--teal-1, #418172)",
                    fontWeight: "700",
                    width: "11em",
                    height: "40px",
                    borderRadius: "4px",
                    border: "1px solid var(--gray-3-stroke, #D7D7D7)",
                  }}
                  onClick={handleDelete}
                >
                  {selectedIds.length} Selected{" "}
                  <span
                    style={{
                      borderLeft: "1px solid #D7D7D7",
                      height: "24px",
                      margin: "0 8px",
                    }}
                  ></span>
                  <FontAwesomeIcon
                    style={{ color: "black" }}
                    icon={faTrashCan}
                  />
                </Button>
              )}
            </div>
          </Card.Header>
          <DataTable
            data={hasData ? openRouterKeys : [{} as OpenRouterKey]}
            columns={columns}
            selectable={hasData}
            bordered
            striped
            hover
            totalPages={pageMeta.pageCount}
            totalRows={pageMeta.recordCount}
            currentPage={pageNumber}
            setCurrentPage={setPageNumber}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            paginationEnabled={false}
            pageSizeEnabled={false}
          />
        </Card>
        <ConfirmationModal
          title="Confirm Delete"
          message={`Are you sure that you want to delete ${selectedIds.length} open router keys?`}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          show={showConfirmationModal}
          onHide={handleCancel}
        />
      </div>
    );
};

export default List;
